import React, { useState, useContext } from 'react';
import {
  Box,
  Container,
  Button,
  Typography,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link as MuiLink,
} from '@mui/material';
import { Link, useHistory } from 'react-router-dom';
import { IdentityContext } from '@react-beets/identity';
import identityService from '../../services/identityService';
import { ROUTES } from '../../constants';
import '../IdentityLogin/styles.css';

function ManagePersonalData() {
  const { identity: user, logout } = useContext(IdentityContext);
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDownloadData = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await identityService.getPersonalData(user?.access_token);

      // Create a downloadable JSON file
      const dataStr = JSON.stringify(data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);

      // Create download link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `rentbeets-personal-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to download your personal data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteDialog = () => {
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
  };

  const handleDeleteAccount = async () => {
    try {
      setDeleting(true);
      setError('');
      await identityService.deleteAccount(user?.access_token);

      // Close dialog and sign out
      setDeleteDialogOpen(false);

      // Sign out and redirect to home
      logout();
      window.location.href = '/';
    } catch (err) {
      setError('Failed to delete your account');
      setDeleting(false);
    }
  };

  return (
    <div className="identity-page">
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography
              component="h2"
              variant="h5"
              align="center"
              sx={{ marginBottom: 3 }}
            >
              Personal Data
            </Typography>

            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ marginBottom: 4 }}>
              <Typography variant="h6" gutterBottom>
                Download Your Data
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                You can download a copy of all your personal data stored in RentBeets. This includes your profile information and account details.
              </Typography>
              <Button
                variant="outlined"
                fullWidth
                sx={{
                  borderColor: '#8B3040',
                  color: '#8B3040',
                  '&:hover': {
                    borderColor: '#6d2533',
                    backgroundColor: 'rgba(139, 48, 64, 0.04)',
                  },
                }}
                onClick={handleDownloadData}
                disabled={loading}
              >
                {loading ? 'Preparing Download...' : 'Download Personal Data'}
              </Button>
            </Box>

            <Box sx={{ marginBottom: 4 }}>
              <Typography variant="h6" gutterBottom color="error">
                Delete Your Account
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Deleting your account will permanently remove all your personal data from RentBeets. This action cannot be undone.
              </Typography>
              <Button
                variant="contained"
                fullWidth
                color="error"
                onClick={handleOpenDeleteDialog}
              >
                Delete My Account
              </Button>
            </Box>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2">
                <MuiLink
                  component={Link}
                  to={ROUTES.MANAGE_PROFILE}
                  sx={{ color: '#8B3040', fontWeight: 'bold' }}
                >
                  Back to Profile
                </MuiLink>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Delete Account?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete your account? This will permanently remove all your personal data from RentBeets and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteAccount} color="error" autoFocus disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default ManagePersonalData;
